import { useState, useEffect } from "react";
import { listTools, getDeveloperConnections, getVaultwardenStatus, connectVaultwarden, disconnectVaultwarden } from "../lib/api";
import { getConnections, connectGoogle } from "../lib/auth";

/* ── Service Catalog ─────────────────────────────────────────── */

type ServiceStatus = "connected" | "available" | "coming_soon";
type ServiceKind = "productivity" | "infra" | "utility" | "communication" | "developer" | "storage";

interface ServiceDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  kind: ServiceKind;
  capabilities: string[];
  status: ServiceStatus;
  connectType: "oauth" | "auto" | "config" | "none";
  toolPrefix?: string; // matches tool names starting with this
}

const SERVICE_CATALOG: ServiceDef[] = [
  // ── Google Services (OAuth) ──
  { id: "gmail", name: "Gmail", description: "Search, read, and send emails from your Google account.", icon: "📧", kind: "productivity", capabilities: ["gmail_search", "gmail_read", "gmail_send"], status: "available", connectType: "oauth", toolPrefix: "gmail_" },
  { id: "gcalendar", name: "Google Calendar", description: "View upcoming events and create new calendar entries.", icon: "📅", kind: "productivity", capabilities: ["calendar_list", "calendar_create"], status: "available", connectType: "oauth", toolPrefix: "calendar_" },
  { id: "gdrive", name: "Google Drive", description: "Search and read files from your Google Drive.", icon: "📁", kind: "storage", capabilities: ["drive_search", "drive_read"], status: "available", connectType: "oauth", toolPrefix: "drive_" },
  // ── INFRA Platform (auto-connected via service bindings) ──
  { id: "infra_health", name: "Health Monitor", description: "Check status of all platform services, products, and infrastructure.", icon: "💚", kind: "infra", capabilities: ["infra_health"], status: "connected", connectType: "auto", toolPrefix: "infra_health" },
  { id: "infra_users", name: "User Management", description: "List and search users across the INFRA auth service.", icon: "👥", kind: "infra", capabilities: ["infra_users"], status: "connected", connectType: "auto", toolPrefix: "infra_users" },
  { id: "infra_tests", name: "Test Runner", description: "Trigger and monitor integration test suites across all services.", icon: "🧪", kind: "infra", capabilities: ["infra_test_run", "infra_test_status"], status: "connected", connectType: "auto", toolPrefix: "infra_test" },
  { id: "infra_backups", name: "Backup Manager", description: "Check backup status and trigger manual D1 database backups.", icon: "💾", kind: "infra", capabilities: ["infra_backup_status", "infra_backup_run"], status: "connected", connectType: "auto", toolPrefix: "infra_backup" },
  { id: "infra_secrets", name: "Secret Registry", description: "View secret expiry status across all services. Never reveals values.", icon: "🔐", kind: "infra", capabilities: ["infra_secrets"], status: "connected", connectType: "auto", toolPrefix: "infra_secrets" },
  // ── Built-in Utilities (always available) ──
  { id: "web_fetch", name: "Web Browser", description: "Fetch web pages, call REST APIs, and download data from any URL.", icon: "🌐", kind: "utility", capabilities: ["web_fetch"], status: "connected", connectType: "auto" },
  { id: "calculator", name: "Calculator", description: "Evaluate math expressions, percentages, and unit conversions.", icon: "🧮", kind: "utility", capabilities: ["calculator"], status: "connected", connectType: "auto" },
  { id: "time", name: "Clock & Timezone", description: "Get current date/time in any timezone worldwide.", icon: "🕐", kind: "utility", capabilities: ["current_time"], status: "connected", connectType: "auto" },
  // ── Coming Soon ──
  { id: "slack", name: "Slack", description: "Search messages, post to channels, and manage notifications.", icon: "💬", kind: "communication", capabilities: [], status: "coming_soon", connectType: "oauth" },
  { id: "notion", name: "Notion", description: "Search and read pages, databases, and knowledge bases.", icon: "📝", kind: "productivity", capabilities: [], status: "coming_soon", connectType: "oauth" },
  { id: "github", name: "GitHub", description: "Browse repos, read/write code, create branches and PRs, search across all your repositories.", icon: "🐙", kind: "developer", capabilities: ["github_repo_tree", "github_read_file", "github_search_code", "github_create_branch", "github_commit_file", "github_create_pr", "github_diff"], status: "available", connectType: "config", toolPrefix: "github_" },
  { id: "linear", name: "Linear", description: "Create and manage issues, projects, and engineering cycles.", icon: "📐", kind: "developer", capabilities: [], status: "coming_soon", connectType: "oauth" },
  { id: "spotify", name: "Spotify", description: "Search music, manage playlists, and control playback.", icon: "🎵", kind: "productivity", capabilities: [], status: "coming_soon", connectType: "oauth" },
  { id: "home_assistant", name: "Home Assistant", description: "Control smart home devices, automations, and sensors.", icon: "🏠", kind: "utility", capabilities: [], status: "coming_soon", connectType: "config" },
  // ── Password Management ──
  { id: "vaultwarden", name: "Vaultwarden", description: "Search your Bitwarden/Vaultwarden vault and retrieve credentials. Password retrieval requires your explicit approval.", icon: "🔐", kind: "utility", capabilities: ["vaultwarden_search", "vaultwarden_get_password"], status: "available", connectType: "config", toolPrefix: "vaultwarden_" },
];

const KIND_LABELS: Record<ServiceKind, string> = {
  productivity: "Productivity",
  infra: "Infrastructure",
  utility: "Utility",
  communication: "Communication",
  developer: "Developer",
  storage: "Storage",
};

const KIND_COLORS: Record<ServiceKind, string> = {
  productivity: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  infra: "bg-accent/10 text-accent border-accent/20",
  utility: "bg-success/10 text-success border-success/20",
  communication: "bg-warning/10 text-warning border-warning/20",
  developer: "bg-muted text-muted-foreground border-border",
  storage: "bg-primary/10 text-primary border-primary/20",
};

const CONFIRM_CAPABILITIES = new Set(["gmail_send", "calendar_create", "infra_test_run", "infra_backup_run", "vaultwarden_get_password"]);

/* ── Component ───────────────────────────────────────────────── */

interface IntegrationsViewProps {
  onAskAI?: (context: string) => void;
}

export function IntegrationsView({ onAskAI }: IntegrationsViewProps) {
  const [services, setServices] = useState<ServiceDef[]>(SERVICE_CATALOG);
  const [tools, setTools] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"all" | "connected" | "available">("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [vwModal, setVwModal] = useState(false);
  const [vwForm, setVwForm] = useState({ vw_url: "https://vaultwarden.mattherbert.ca", client_id: "", client_secret: "" });
  const [vwConnecting, setVwConnecting] = useState(false);
  const [vwError, setVwError] = useState<string | null>(null);
  const [vwUrl, setVwUrl] = useState<string | null>(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [connData, toolData, vwStatus] = await Promise.all([
      getConnections().catch(() => ({ connections: [] })),
      listTools("ai").catch(() => ({ tools: [] })),
      getVaultwardenStatus().catch(() => ({ connected: false, source: "none", vw_url: null })),
    ]);

    const googleConnected = (connData.connections || []).some((c: any) => c.provider === "google");
    const googleEmail = (connData.connections || []).find((c: any) => c.provider === "google")?.provider_email;
    const devConns = await getDeveloperConnections().catch(() => ({ connections: [] }));
    const githubConnected = (devConns.connections || []).some((c: any) => c.provider === "github" && c.status === "active");
    const vwConnected = vwStatus.connected;
    if (vwConnected && vwStatus.vw_url) setVwUrl(vwStatus.vw_url);

    setTools(toolData.tools || []);
    setServices((prev) =>
      prev.map((s) => {
        if (["gmail", "gcalendar", "gdrive"].includes(s.id))
          return { ...s, status: googleConnected ? "connected" as const : "available" as const };
        if (s.id === "github")
          return { ...s, status: githubConnected ? "connected" as const : "available" as const };
        if (s.id === "vaultwarden")
          return { ...s, status: vwConnected ? "connected" as const : "available" as const };
        return s;
      })
    );
    if (googleEmail) (window as any).__googleEmail = googleEmail;
    setLoading(false);
  };

  const handleVwConnect = async () => {
    if (!vwForm.vw_url || !vwForm.client_id || !vwForm.client_secret) {
      setVwError("All fields are required."); return;
    }
    setVwConnecting(true); setVwError(null);
    try {
      const res = await connectVaultwarden(vwForm);
      setVwUrl(res.vw_url);
      setServices((prev) => prev.map((s) => s.id === "vaultwarden" ? { ...s, status: "connected" as const } : s));
      setVwModal(false);
    } catch (e: any) {
      setVwError(e.message || "Connection failed. Check your credentials.");
    } finally { setVwConnecting(false); }
  };

  const handleVwDisconnect = async () => {
    await disconnectVaultwarden().catch(() => {});
    setVwUrl(null);
    setServices((prev) => prev.map((s) => s.id === "vaultwarden" ? { ...s, status: "available" as const } : s));
  };

  const handleConnect = (service: ServiceDef) => {
    if (service.connectType === "oauth" && ["gmail", "gcalendar", "gdrive"].includes(service.id)) connectGoogle();
    if (service.id === "vaultwarden") { setVwError(null); setVwModal(true); }
  };

  const connectedServices = services.filter((s) => s.status === "connected");
  const availableServices = services.filter((s) => s.status === "available");
  const comingSoonServices = services.filter((s) => s.status === "coming_soon");

  const filtered = tab === "connected" ? connectedServices : tab === "available" ? [...availableServices, ...comingSoonServices] : services;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-foreground">Integrations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Connect services so the AI can access them during conversations.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-success" />
                <span className="text-muted-foreground">{connectedServices.length} connected</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">{availableServices.length} available</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                <span className="text-muted-foreground">{comingSoonServices.length} coming soon</span>
              </div>
              <div className="text-muted-foreground">
                {tools.length} tools active
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-card rounded-lg p-1 w-fit border border-border">
              {(["all", "connected", "available"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    tab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === "all" ? `All (${services.length})` : t === "connected" ? `Connected (${connectedServices.length})` : `Available (${availableServices.length + comingSoonServices.length})`}
                </button>
              ))}
            </div>

            {/* Service Grid */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  expanded={expandedId === service.id}
                  onToggleExpand={() => setExpandedId(expandedId === service.id ? null : service.id)}
                  onConnect={() => handleConnect(service)}
                  onDisconnect={service.id === "vaultwarden" ? handleVwDisconnect : undefined}
                  onTryInChat={() => {
                    const cap = service.capabilities[0];
                    if (cap) onAskAI?.(`Use the ${cap} tool`);
                  }}
                  tools={tools}
                  extraInfo={service.id === "vaultwarden" && vwUrl ? vwUrl : undefined}
                />
              ))}
            </div>

            {/* Vaultwarden config modal */}
            {vwModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4">
                  <h2 className="text-base font-semibold text-foreground">Connect Vaultwarden</h2>
                  <p className="text-xs text-muted-foreground">Generate API credentials in <strong>Account Settings → Security → API Key</strong>.</p>
                  <div className="space-y-3">
                    {([{ label: "Vaultwarden URL", key: "vw_url", type: "text", placeholder: "https://vaultwarden.example.com" },
                      { label: "Client ID", key: "client_id", type: "text", placeholder: "user.xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
                      { label: "Client Secret", key: "client_secret", type: "password", placeholder: "Your client secret" }] as const)
                      .map(({ label, key, type, placeholder }) => (
                        <div key={key}>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
                          <input type={type} placeholder={placeholder}
                            className="w-full rounded-lg bg-background border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                            value={vwForm[key]}
                            onChange={(e) => setVwForm((f) => ({ ...f, [key]: e.target.value }))}
                          />
                        </div>
                      ))}
                  </div>
                  {vwError && <p className="text-xs text-red-400">{vwError}</p>}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => setVwModal(false)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-background transition-colors">Cancel</button>
                    <button onClick={handleVwConnect} disabled={vwConnecting}
                      className="flex-1 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors">
                      {vwConnecting ? "Connecting…" : "Connect"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Service Card ────────────────────────────────────────────── */

function ServiceCard({
  service,
  expanded,
  onToggleExpand,
  onConnect,
  onDisconnect,
  onTryInChat,
  tools,
  extraInfo,
}: {
  service: ServiceDef;
  expanded: boolean;
  onToggleExpand: () => void;
  onConnect: () => void;
  onDisconnect?: () => void;
  onTryInChat: () => void;
  tools: any[];
  extraInfo?: string;
}) {
  const isComingSoon = service.status === "coming_soon";
  const isConnected = service.status === "connected";
  const googleEmail = (window as any).__googleEmail;

  // Find matching tools
  const matchingTools = service.toolPrefix
    ? tools.filter((t) => t.name.startsWith(service.toolPrefix!))
    : tools.filter((t) => service.capabilities.includes(t.name));

  return (
    <div className={`bg-card border border-border rounded-xl transition-colors ${isComingSoon ? "opacity-50" : ""}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center text-xl">
              {service.icon}
            </div>
            <div>
              <h3 className="font-medium text-foreground text-sm">{service.name}</h3>
              <span className={`inline-block mt-0.5 px-1.5 py-0 text-[10px] rounded border ${KIND_COLORS[service.kind]}`}>
                {KIND_LABELS[service.kind]}
              </span>
            </div>
          </div>
          {isConnected && (
            <div className="flex items-center gap-1 text-xs text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              Connected
            </div>
          )}
          {isComingSoon && (
            <span className="text-xs text-muted-foreground">Coming soon</span>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{service.description}</p>

        {/* Connected account info */}
        {isConnected && ["gmail", "gcalendar", "gdrive"].includes(service.id) && googleEmail && (
          <div className="text-xs text-muted-foreground mb-3">{googleEmail}</div>
        )}
        {isConnected && extraInfo && (
          <div className="text-xs text-muted-foreground mb-3 truncate">{extraInfo}</div>
        )}

        {/* Capabilities */}
        {service.capabilities.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {service.capabilities.map((cap) => (
              <span
                key={cap}
                className={`px-1.5 py-0.5 text-[10px] rounded font-mono ${
                  CONFIRM_CAPABILITIES.has(cap)
                    ? "bg-warning/10 text-warning border border-warning/20"
                    : "bg-background text-muted-foreground border border-border"
                }`}
              >
                {cap}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        {isConnected ? (
          <div className="flex gap-2">
            {onDisconnect ? (
              <button onClick={onDisconnect}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-red-400 border border-red-900/40 rounded-lg hover:bg-red-950/30 transition-colors">Disconnect</button>
            ) : (
              <button onClick={onToggleExpand}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-background transition-colors">
                {expanded ? "Hide details" : "Details"}
              </button>
            )}
            <button
              onClick={onTryInChat}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
            >
              Try in chat
            </button>
          </div>
        ) : isComingSoon ? (
          <button disabled className="w-full px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg opacity-50 cursor-not-allowed">
            Coming Soon
          </button>
        ) : (
          <button
            onClick={onConnect}
            className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Connect
          </button>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && matchingTools.length > 0 && (
        <div className="border-t border-border p-4 space-y-2">
          <div className="text-xs font-medium text-muted-foreground">Available tools:</div>
          {matchingTools.map((tool: any) => (
            <div key={tool.name} className="text-xs">
              <div className="flex items-center gap-2">
                <code className="font-mono text-foreground bg-background px-1.5 py-0.5 rounded">{tool.name}</code>
                {CONFIRM_CAPABILITIES.has(tool.name) && (
                  <span className="text-[9px] text-warning">requires approval</span>
                )}
              </div>
              <p className="text-muted-foreground mt-0.5 leading-relaxed">{tool.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
